import { useCallback, useEffect, useRef, useState } from "react";

// Web port of HorizontalTileListView
// (LibraryFeature/View/Component/HorizontalTileListView.swift).
//
// The list bleeds to the edges of its container and carries its own 16px
// inner padding, which is why the app's home feed deliberately does *not*
// apply horizontal padding to scroller sections.
//
// On top of the app's behaviour, the edges fade to the page background so
// tiles look like they dissolve rather than getting cut off. Each fade only
// appears when there is actually more content that way — a permanent fade
// over the first tile would read as a rendering bug.
//
// The scroll container is deliberately NOT focusable. Every tile inside is a
// real button, so keyboard users already reach the content and the browser
// scrolls each tile into view as they tab; a scrollable region only needs its
// own tabindex when it has no focusable children. Making it focusable also
// drew a focus ring around the whole row in Safari, which treats click focus
// on a tabindex'd div as :focus-visible even though it does not for buttons.

const HORIZONTAL_PADDING = "px-4";
const TILE_SPACING = "gap-4";
const FADE_WIDTH = "w-10";

export interface HorizontalTileListProps<Item> {
  items: Item[];
  getKey: (item: Item) => string;
  renderTile: (item: Item) => React.ReactNode;
  /** Labels the scrollable region for assistive tech. */
  label: string;
}

export default function HorizontalTileList<Item>(props: HorizontalTileListProps<Item>) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const sync = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;

    // Sub-pixel layout means scrollLeft rarely hits the bounds exactly.
    const maxScroll = el.scrollWidth - el.clientWidth;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft >= maxScroll - 1);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    sync();
    el.addEventListener("scroll", sync, { passive: true });

    // Catches container resizes and late-loading artwork, both of which
    // change whether there is anything left to scroll to.
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    for (const child of Array.from(el.children)) observer.observe(child);

    return () => {
      el.removeEventListener("scroll", sync);
      observer.disconnect();
    };
  }, [sync, props.items]);

  return <div className="relative">
    <div
      ref={scrollerRef}
      role="group"
      aria-label={props.label}
      className={`no-scrollbar flex items-start overflow-x-auto ${TILE_SPACING} ${HORIZONTAL_PADDING}`}
    >
      {props.items.map((item) => (
        <div key={props.getKey(item)}>{props.renderTile(item)}</div>
      ))}
    </div>

    <EdgeFade side="left" visible={!atStart} />
    <EdgeFade side="right" visible={!atEnd} />
  </div>
}

function EdgeFade(props: { side: "left" | "right", visible: boolean }) {
  const position = props.side === "left"
    ? `left-0 bg-linear-to-r`
    : `right-0 bg-linear-to-l`;

  return <div
    aria-hidden="true"
    className={`pointer-events-none absolute inset-y-0 ${position} ${FADE_WIDTH} from-page to-transparent transition-opacity duration-200 ${props.visible ? "opacity-100" : "opacity-0"}`}
  />
}
